import { useEffect, useState } from 'react';

import { Action, Index } from './models';
import useActivityLogger from './useActivityLogger';

interface MediaConstraints {
  audio: boolean;
  video?: boolean;
}

const usePermission = () => {
  const [hasMediaPermission, setMediaPermission] = useState<boolean>(false);
  const [isWaiting, setWaiting] = useState<boolean>(false);
  const [isPermited, setPermited] = useState<{ audio: boolean; video: boolean }>({
    audio: false,
    video: false
  });
  const { log } = useActivityLogger();

  const checkPermissions = async () => {
    try {
      // @ts-ignore
      const micPermission = await navigator.permissions.query({ name: 'microphone' });
      // @ts-ignore
      const cameraPermission = await navigator.permissions.query({ name: 'camera' });

      setMediaPermission(micPermission.state === 'granted' || micPermission.state !== 'prompt');
      setPermited({
        audio: micPermission.state === 'granted',
        video: cameraPermission.state === 'granted'
      });
    } catch (error) {
      console.warn('Error while checking permissions:', error);

      void log({
        action: Action.ERROR,
        description: `'Error while checking permissions, error message: ${(error as Error).message}`,
        index: Index.ACTIVITY,
        label: 'ERROR'
      });

      setMediaPermission(false);
      setPermited({
        audio: false,
        video: false
      });
    }
  };

  const onClickPermission = async (isAudioOnly?: boolean) => {
    try {
      setWaiting(true);

      const mediaOptions: MediaConstraints = { audio: true };
      if (!isAudioOnly) {
        mediaOptions.video = true;
      }

      const stream = await navigator.mediaDevices.getUserMedia(mediaOptions);

      setMediaPermission(stream.active);
      setPermited({
        audio: true,
        video: !isAudioOnly
      });
    } catch (error) {
      const errorType = error as { name: string };
      if (errorType.name === 'NotAllowedError') {
        setPermited({
          audio: false,
          video: false
        });
      }

      setMediaPermission(true);
      setWaiting(false);
      localStorage.setItem('mediaPermission', 'true');
    } finally {
      void log({
        action: Action.PERMISSION_REQUESTED,
        description: 'Audio Video Permission Requested.',
        index: Index.ACTIVITY,
        label: 'PERMISSION_REQUESTED'
      });
    }
  };

  useEffect(() => {
    if (!hasMediaPermission) {
      void checkPermissions();
    }
  }, [checkPermissions]);

  return {
    hasMediaPermission,
    isPermited,
    isWaiting,
    onClickPermission
  };
};

export default usePermission;
